import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { emptyRO, toPayload } from "@/lib/roMeta";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

function Field({ label, children, span }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export function RepairOrderDialog({ open, onOpenChange, record, onSaved }) {
  const [form, setForm] = useState(emptyRO());
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(record?.id);

  useEffect(() => {
    if (open) {
      if (record) {
        const base = emptyRO();
        Object.keys(base).forEach((k) => {
          base[k] = record[k] ?? (base[k] === "" ? "" : base[k]);
          if (base[k] === null) base[k] = "";
        });
        setForm(base);
      } else {
        setForm(emptyRO());
      }
    }
  }, [open, record]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.ro_number.trim()) {
      toast.error("RO Number is required");
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (isEdit) {
        await api.put(`/repair-orders/${record.id}`, payload);
        toast.success(`RO #${form.ro_number} updated`);
      } else {
        await api.post("/repair-orders", payload);
        toast.success(`RO #${form.ro_number} created`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "bg-secondary/40 border-border";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-card" data-testid="ro-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">
            {isEdit ? `Edit Repair Order #${record.ro_number}` : "New Repair Order Audit"}
          </DialogTitle>
          <DialogDescription>Complete the vehicle repair order details. A compliance score &amp; status are calculated automatically from the fields.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="RO Number *">
            <Input data-testid="ro-input-ro_number" className={inputCls} value={form.ro_number} onChange={set("ro_number")} placeholder="RO-100234" />
          </Field>
          <Field label="VIN (17 chars)">
            <Input data-testid="ro-input-vin" className={inputCls} value={form.vin} onChange={set("vin")} placeholder="1HGCM82633A004352" />
          </Field>
          <Field label="Customer Name">
            <Input data-testid="ro-input-customer_name" className={inputCls} value={form.customer_name} onChange={set("customer_name")} />
          </Field>
          <Field label="RO Date">
            <Input type="date" data-testid="ro-input-ro_date" className={inputCls} value={form.ro_date} onChange={set("ro_date")} />
          </Field>
          <Field label="Vehicle Year">
            <Input data-testid="ro-input-vehicle_year" className={inputCls} value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2022" />
          </Field>
          <Field label="Make">
            <Input data-testid="ro-input-vehicle_make" className={inputCls} value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Toyota" />
          </Field>
          <Field label="Model">
            <Input data-testid="ro-input-vehicle_model" className={inputCls} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="Camry" />
          </Field>
          <Field label="Service Advisor">
            <Input data-testid="ro-input-service_advisor" className={inputCls} value={form.service_advisor} onChange={set("service_advisor")} />
          </Field>
          <Field label="Mileage In">
            <Input type="number" data-testid="ro-input-mileage_in" className={inputCls} value={form.mileage_in} onChange={set("mileage_in")} />
          </Field>
          <Field label="Mileage Out">
            <Input type="number" data-testid="ro-input-mileage_out" className={inputCls} value={form.mileage_out} onChange={set("mileage_out")} />
          </Field>
          <Field label="Technician ID">
            <Input data-testid="ro-input-technician_id" className={inputCls} value={form.technician_id} onChange={set("technician_id")} />
          </Field>
          <Field label="Technician Signature">
            <Select value={form.signature_status} onValueChange={set("signature_status")}>
              <SelectTrigger data-testid="ro-select-signature" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="signed">Signed</SelectItem>
                <SelectItem value="unsigned">Unsigned</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Labor Op Code">
            <Input data-testid="ro-input-labor_op_code" className={inputCls} value={form.labor_op_code} onChange={set("labor_op_code")} placeholder="A12" />
          </Field>
          <Field label="Labor Hours">
            <Input type="number" step="0.1" data-testid="ro-input-labor_hours" className={inputCls} value={form.labor_hours} onChange={set("labor_hours")} />
          </Field>
          <Field label="Claim Amount ($)">
            <Input type="number" step="0.01" data-testid="ro-input-claim_amount" className={inputCls} value={form.claim_amount} onChange={set("claim_amount")} />
          </Field>
          <Field label="Diagnostic Reading / DTC">
            <Input data-testid="ro-input-diagnostic_reading" className={inputCls} value={form.diagnostic_reading} onChange={set("diagnostic_reading")} placeholder="e.g. P0301 misfire cyl 1" />
          </Field>
          <Field label="Removed Part Serial #">
            <Input data-testid="ro-input-part_serial" className={inputCls} value={form.part_serial} onChange={set("part_serial")} placeholder="e.g. CMP-99182" />
          </Field>
        </div>

        <div className="mt-2 rounded-lg border border-border bg-secondary/30 p-4">
          <p className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5" /> The 3 C's — Warranty Story
          </p>
          <div className="space-y-3">
            <Field label="Complaint">
              <Textarea data-testid="ro-input-complaint" className={inputCls} rows={2} value={form.complaint} onChange={set("complaint")} />
            </Field>
            <Field label="Cause">
              <Textarea data-testid="ro-input-cause" className={inputCls} rows={2} value={form.cause} onChange={set("cause")} />
            </Field>
            <Field label="Correction">
              <Textarea data-testid="ro-input-correction" className={inputCls} rows={2} value={form.correction} onChange={set("correction")} />
            </Field>
          </div>
        </div>

        <Field label="Audit Notes">
          <Textarea data-testid="ro-input-notes" className={inputCls} rows={2} value={form.notes} onChange={set("notes")} />
        </Field>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="ro-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ro-save-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? "Save Changes" : "Create Audit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
